/**
 * System report rows for the Report tab and the report dialog over the
 * analysis builder (systemReport.buildReportRows): the hierarchy, the derived
 * status, the health score, the lifecycle estimate and the spare-parts
 * lookup come from there; this maps them onto the shape the table renders,
 * fills the spare-parts cell for subsystems the builder's keyword rule
 * misses (LNA, drive system, waveguide, see equipmentKeyFor) and exposes the
 * CSV export.
 */
import {
  buildReportRows,
  COMPONENT_LIFETIMES,
  csvFileName,
  toCsv,
  type ComponentLifetime,
  type InventoryManager,
  type InventoryStatus,
  type ReportGroup as AnalysisGroup,
  type ReportPrediction,
  type ReportRow as AnalysisRow,
  type ReportSummary,
} from '@/features/twin/analysis';
import type { Element, Reading, StatusColour } from '../../types';
import { parseDateMs } from '../lib/format';
import { equipmentKeyFor } from './inventory';

export type { ReportPrediction, ReportSummary };
export { csvFileName };

export type ReportItemType = 'unit' | 'component' | 'subcomponent';

export const DEFAULT_LIFETIME: ComponentLifetime = { baseLifetime: 10, mtbfHours: 90000, category: 'Other' };

/** Exact key first, then the first keyword contained in the id or name. */
export function lifetimeFor(componentId: string, name: string = ''): ComponentLifetime {
  const id = componentId.toLowerCase();
  if (COMPONENT_LIFETIMES[id]) return COMPONENT_LIFETIMES[id];
  const text = `${id} ${name.toLowerCase()}`;
  for (const [key, lifetime] of Object.entries(COMPONENT_LIFETIMES)) {
    if (key === 'default') continue;
    if (text.includes(key)) return lifetime;
  }
  return COMPONENT_LIFETIMES.default ?? DEFAULT_LIFETIME;
}

export interface ReportRow {
  id: string;
  name: string;
  parentLabel: string;
  itemType: ReportItemType;
  category: string;
  /** OPERATIONAL, WARNING, DEGRADED, OFFLINE or CRITICAL. */
  level: string;
  colour: StatusColour;
  health: number;
  healthTrend: number;
  lifecycle: { days: number; dateMs: number; reason: string } | null;
  inventory: InventoryStatus | null;
  prediction: ReportPrediction | null;
  /** Direct component count for unit rows. */
  componentCount: number;
  /** The analysis row, for the CSV and the AI service. */
  source: AnalysisRow;
}

export interface ReportGroup {
  id: string;
  name: string;
  units: Array<{ row: ReportRow; components: ReportRow[] }>;
}

export interface ReportModel {
  groups: ReportGroup[];
  /** Flat list in the builder's order (a unit's components, then the unit). */
  rows: ReportRow[];
  counts: { total: number; operational: number; warning: number; critical: number };
  summary: ReportSummary;
}

export interface ReportSources {
  readings: Record<string, Reading>;
  history: Record<string, Reading[]>;
  inventory: InventoryManager | null;
  nowMs: number;
  /** Per-component predictions already made (the AI column and the CSV). */
  aiPredictions?: Record<string, ReportPrediction>;
}

function mapRow(row: AnalysisRow, inventory: InventoryManager | null): ReportRow {
  let stock = row.inventory;
  if (!stock && inventory) {
    const key = equipmentKeyFor(row.id);
    stock = key ? inventory.getInventoryForEquipment(key) : null;
  }
  const lifecycle =
    row.timeToReplacement !== null && row.predictedReplacementMs !== null ? { days: row.timeToReplacement, dateMs: row.predictedReplacementMs, reason: row.replacementReason } : null;
  return {
    id: row.id,
    name: row.name,
    parentLabel: row.parentLabel,
    itemType: row.itemType,
    category: row.category,
    level: row.status,
    colour: row.statusColour,
    health: row.health,
    healthTrend: row.healthTrend,
    lifecycle,
    inventory: stock,
    prediction: row.aiPrediction,
    componentCount: row.components?.filter((c) => c.itemType === 'component').length ?? 0,
    source: row,
  };
}

function mapGroup(group: AnalysisGroup, inventory: InventoryManager | null): ReportGroup {
  return {
    id: group.id,
    name: group.name,
    units: group.units.map((unit) => ({ row: mapRow(unit, inventory), components: (unit.components ?? []).map((c) => mapRow(c, inventory)) })),
  };
}

/** The report for an element under the current readings, inventory and predictions. */
export function buildReportModel(element: Element, sources: ReportSources): ReportModel {
  const report = buildReportRows(element, {
    readings: sources.readings,
    history: sources.history,
    inventory: sources.inventory,
    nowMs: sources.nowMs,
    aiPredictions: sources.aiPredictions,
  });
  const rows = report.rows.map((r) => mapRow(r, sources.inventory));
  return {
    groups: report.groups.map((g) => mapGroup(g, sources.inventory)),
    rows,
    counts: { total: report.summary.total, operational: report.summary.operational, warning: report.summary.warning, critical: report.summary.critical },
    summary: report.summary,
  };
}

/** The 12-column CSV of the builder, with the predictions merged into the rows. */
export function reportCsv(element: Element, sources: ReportSources): string {
  const report = buildReportRows(element, {
    readings: sources.readings,
    history: sources.history,
    inventory: sources.inventory,
    nowMs: sources.nowMs,
    aiPredictions: sources.aiPredictions,
  });
  return toCsv(report.rows);
}

/** ms for a prediction's YYYY-MM-DD date (engine or AI field), null when absent. */
export function predictionDateMs(prediction: ReportPrediction | null | undefined): number | null {
  if (!prediction) return null;
  return parseDateMs(prediction.predictedFailureDate ?? prediction.predictedFailure ?? null);
}
